import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
} from '@mui/material';
// Using Box grid instead of Grid to avoid TS type issues
import { CheckCircle, Logout } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { JoinExistingOrganizationCard } from '../components/onboarding/JoinExistingOrganizationCard';

const OnboardingSelectTypePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useAppTranslation();
  const [selectedType, setSelectedType] = useState<'store' | 'vendor' | 'group' | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleSelectType = (type: 'store' | 'vendor' | 'group') => {
    setSelectedType(type);
    setError(null);
  };

  const handleContinue = async () => {
    if (!selectedType) {
      setError('Please select your organization type');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/onboarding/select-type', {
        method: 'POST',
        headers,
        body: JSON.stringify({ userType: selectedType }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to set user type');
      }

      await response.json();

      // Navigate to appropriate setup page
      if (selectedType === 'store') {
        navigate('/onboarding/setup-store');
      } else if (selectedType === 'vendor') {
        navigate('/onboarding/setup-vendor');
      } else if (selectedType === 'group') {
        navigate('/onboarding/setup-group');
      }
    } catch (err) {
      console.error('Error selecting user type:', err);
      setError(err instanceof Error ? err.message : 'Failed to set user type. Please try again.');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    setError(null);
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to log out:', err);
      setError('Failed to log out. Please try again.');
      setLoggingOut(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      pt: { xs: 6, md: 10 },
      pb: { xs: 6, md: 10 },
    }}>
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="text"
            startIcon={<Logout />}
            onClick={handleLogout}
            disabled={loggingOut}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {loggingOut ? 'Signing out...' : 'Sign out'}
          </Button>
        </Box>
        <Box textAlign="center" mb={5}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Welcome! Let's Get Started
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tell us about your organization so we can tailor the setup for you.
          </Typography>
        </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={4}>
          <Card
            sx={{
              position: 'relative',
              cursor: 'pointer',
              borderRadius: 3,
              border: '2px solid',
              borderColor: selectedType === 'store' ? 'primary.main' : 'divider',
              backgroundColor: 'background.paper',
              boxShadow: selectedType === 'store' ? 6 : 1,
              transition: 'all .2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
                borderColor: selectedType === 'store' ? 'primary.main' : 'primary.light',
              },
            }}
            onClick={() => handleSelectType('store')}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box mb={2}>
                <svg width="100" height="70" viewBox="0 0 200 150" aria-hidden>
                  <rect x="10" y="50" rx="12" width="180" height="60" fill={theme.palette.primary.light} opacity="0.25" />
                  <rect x="30" y="30" rx="10" width="60" height="40" fill={theme.palette.primary.main} />
                  <circle cx="60" cy="50" r="8" fill="#fff" />
                  <rect x="110" y="35" rx="8" width="60" height="35" fill={theme.palette.primary.dark} />
                  <rect x="45" y="95" rx="6" width="110" height="10" fill={theme.palette.primary.main} opacity="0.6" />
                </svg>
              </Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Camera Store / Retailer
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Perfect if you're a retail store or online shop selling cameras and accessories.
              </Typography>
            </CardContent>
            {selectedType === 'store' && (
              <CheckCircle color="primary" sx={{ position: 'absolute', top: 16, right: 16, fontSize: 28 }} />
            )}
          </Card>

          <Card
            sx={{
              position: 'relative',
              cursor: 'pointer',
              borderRadius: 3,
              border: '2px solid',
              borderColor: selectedType === 'vendor' ? 'primary.main' : 'divider',
              backgroundColor: 'background.paper',
              boxShadow: selectedType === 'vendor' ? 6 : 1,
              transition: 'all .2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
                borderColor: selectedType === 'vendor' ? 'primary.main' : 'primary.light',
              },
            }}
            onClick={() => handleSelectType('vendor')}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box mb={2}>
                <svg width="100" height="70" viewBox="0 0 200 150" aria-hidden>
                  <rect x="15" y="60" rx="12" width="170" height="50" fill={theme.palette.primary.light} opacity="0.25" />
                  <rect x="30" y="40" rx="6" width="45" height="25" fill={theme.palette.primary.dark} />
                  <rect x="80" y="40" rx="6" width="45" height="25" fill={theme.palette.primary.main} />
                  <rect x="130" y="40" rx="6" width="45" height="25" fill={theme.palette.primary.dark} />
                  <rect x="40" y="95" rx="4" width="120" height="8" fill={theme.palette.primary.main} opacity="0.6" />
                </svg>
              </Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Vendor / Manufacturer
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Ideal for brands like Sony, Canon, Nikon, Fujifilm, ProMaster, and other manufacturers.
              </Typography>
            </CardContent>
            {selectedType === 'vendor' && (
              <CheckCircle color="primary" sx={{ position: 'absolute', top: 16, right: 16, fontSize: 28 }} />
            )}
          </Card>

          <Card
            sx={{
              position: 'relative',
              cursor: 'pointer',
              borderRadius: 3,
              border: '2px solid',
              borderColor: selectedType === 'group' ? 'primary.main' : 'divider',
              backgroundColor: 'background.paper',
              boxShadow: selectedType === 'group' ? 6 : 1,
              transition: 'all .2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
                borderColor: selectedType === 'group' ? 'primary.main' : 'primary.light',
              },
            }}
            onClick={() => handleSelectType('group')}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box mb={2}>
                <svg width="100" height="70" viewBox="0 0 200 150" aria-hidden>
                  <rect x="20" y="70" rx="10" width="45" height="45" fill={theme.palette.primary.light} opacity="0.5" />
                  <rect x="75" y="55" rx="10" width="50" height="60" fill={theme.palette.primary.main} />
                  <rect x="135" y="70" rx="10" width="45" height="45" fill={theme.palette.primary.light} opacity="0.5" />
                  <circle cx="100" cy="30" r="14" fill={theme.palette.primary.dark} />
                </svg>
              </Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Buying Group / Head Office
              </Typography>
              <Typography variant="body1" color="text.secondary">
                For a buying group or head office (like Fotosource Canada) that oversees member
                stores, each of which keeps its own account.
              </Typography>
            </CardContent>
            {selectedType === 'group' && (
              <CheckCircle color="primary" sx={{ position: 'absolute', top: 16, right: 16, fontSize: 28 }} />
            )}
          </Card>
        </Box>

        <Box mt={5} display="flex" justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            disabled={!selectedType || loading}
            sx={{ minWidth: 220, fontWeight: 600 }}
          >
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        </Box>

        <Box mt={6}>
          <Box textAlign="center" mb={2}>
            <Typography variant="body2" color="text.secondary">
              {t('onboarding.joinExisting.sectionIntro')}
            </Typography>
          </Box>
          <JoinExistingOrganizationCard />
        </Box>
      </Container>
    </Box>
  );
};

export default OnboardingSelectTypePage;
